import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Bullet from './Bullet';
import HomeTileCard from './HomeTileCard';
import useRetryOnReconnect from './useRetryOnReconnect';

const token = process.env.REACT_APP_WSSR_API_TOKEN;
const PREVIEW_COUNT = 10;
const CAROUSEL_LIMIT = 8;

const formatDuration = (totalSeconds) => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${hours > 0 ? `${hours}h ` : ''}${minutes}m ${seconds}s`;
};

export default function HomeFeedPreview({ carousel = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const mountedRef = useRef(false);

  const fetchPreview = async () => {
    setLoading(true);
    try {
      const endpoint = `https://broadcast.wssr.stream/api/station/1/files/list?sort=timestamp&sortOrder=DESC&currentDirectory=Feed&rowCount=${PREVIEW_COUNT}&current=1`;
      const response = await axios.get(endpoint, { headers: { Authorization: token } });
      const rows = response.data.rows.filter((item) => item.path_short !== 'cover.png');

      // Directories don't carry their own art - look up the first track inside each one.
      const withArt = await Promise.all(
        rows.map(async (item) => {
          if (item.text !== 'Directory') {
            return { ...item, artUrl: item.media?.art ? `https://broadcast.wssr.stream${item.media.art}` : null };
          }
          try {
            // sort=timestamp&sortOrder=DESC + the cover.png precedence below mirrors Feed.js's
            // fetchImageUrl exactly, so a directory's tile here matches its cover on the Feed page.
            const dirEndpoint = `https://broadcast.wssr.stream/api/station/1/files/list?sort=timestamp&sortOrder=DESC&currentDirectory=${encodeURIComponent(item.path)}`;
            const dirResponse = await axios.get(dirEndpoint, { headers: { Authorization: token } });
            const dirData = dirResponse.data;

            const coverTrack = dirData.find((track) => track.path_short === 'cover.png');
            let artUrl = null;
            if (coverTrack?.links?.download) {
              try {
                const imageResponse = await axios.get(
                  `https://broadcast.wssr.stream${coverTrack.links.download}`,
                  { headers: { Authorization: token }, responseType: 'blob' }
                );
                artUrl = URL.createObjectURL(imageResponse.data);
              } catch (imageError) {
                artUrl = null;
              }
            }

            const dirTracks = dirData.filter((track) => track.path_short !== 'cover.png');
            if (!artUrl) {
              const firstTrack = dirTracks[0];
              artUrl = firstTrack?.media?.links?.art
                ? `https://broadcast.wssr.stream${firstTrack.media.links.art}`
                : null;
            }

            const trackCount = dirTracks.length;
            const totalDuration = dirTracks.reduce((acc, track) => acc + (track.media?.length || 0), 0);
            return { ...item, artUrl, trackCount, totalDuration };
          } catch (error) {
            return { ...item, artUrl: null };
          }
        })
      );

      if (mountedRef.current) {
        setItems(withArt);
        setHasError(false);
      }
    } catch (error) {
      console.error('Error fetching feed preview:', error);
      if (mountedRef.current) setHasError(true);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    fetchPreview();
    return () => { mountedRef.current = false; };
  }, []);

  useRetryOnReconnect(fetchPreview, hasError);

  const showSkeleton = loading || (hasError && items.length === 0);

  const getItemMeta = (item) => {
    const isDirectory = item.text === 'Directory';
    const title = isDirectory ? item.path_short : (item.media?.title || item.path_short);
    const linkTo = isDirectory ? `/feed/${encodeURIComponent(item.path)}` : '/feed';
    const tileSubtitle = isDirectory
      ? `${item.trackCount ?? '...'} ${item.trackCount === 1 ? 'Track' : 'Tracks'}`
      : (item.media?.artist || 'Unknown Artist');

    return { isDirectory, title, linkTo, tileSubtitle };
  };

  const renderRow = (item, large) => {
    const { isDirectory, title, linkTo } = getItemMeta(item);

    const subtitle = isDirectory ? (
      <>
        {item.trackCount !== undefined
          ? `${item.trackCount} ${item.trackCount === 1 ? 'Track' : 'Tracks'}`
          : 'Loading...'}
        {Bullet}
        {item.totalDuration !== undefined ? formatDuration(item.totalDuration) : 'Calculating...'}
      </>
    ) : (
      <>
        {item.media?.artist || 'Unknown Artist'}
        {Bullet}
        {formatDuration(item.media?.length || 0)}
      </>
    );

    if (large) {
      return (
        <Link
          key={item.path}
          to={linkTo}
          style={{ textDecoration: 'none', color: 'inherit', display: 'block', marginBottom: 24 }}
        >
          <Box sx={{ width: '100%', paddingTop: '100%', position: 'relative', backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: 1, overflow: 'hidden' }}>
            {item.artUrl && (
              <img
                src={item.artUrl}
                alt={title}
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
              />
            )}
          </Box>
          <Box sx={{ minWidth: 0, marginTop: 1.5 }}>
            <Typography variant="subtitle1" noWrap sx={{ fontWeight: 700 }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="caption" sx={{ color: '#999' }} noWrap>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Link>
      );
    }

    return (
      <Link
        key={item.path}
        to={linkTo}
        style={{
          textDecoration: 'none',
          color: 'inherit',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 10,
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            flexShrink: 0,
            backgroundColor: 'rgba(255,255,255,0.05)',
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          {item.artUrl && (
            <img src={item.artUrl} alt={title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          )}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" noWrap>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" sx={{ color: '#999' }} noWrap>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Link>
    );
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ marginBottom: 1.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Feed</Typography>
        <Link to="/feed" style={{ color: 'inherit', fontSize: '0.85rem', opacity: 0.75 }}>
          See more
        </Link>
      </Box>

      {carousel ? (
        <Box sx={{ mx: { xs: -2, md: 0 }, px: { xs: 2, md: 0 } }}>
          <Box
            sx={{
              display: 'flex',
              gap: 1.5,
              overflowX: 'auto',
              scrollSnapType: 'x mandatory',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            {showSkeleton ? (
              Array.from(new Array(CAROUSEL_LIMIT)).map((_, index) => (
                <Box key={index} sx={{ flexShrink: 0, scrollSnapAlign: 'start' }}>
                  <HomeTileCard loading />
                </Box>
              ))
            ) : items.slice(0, CAROUSEL_LIMIT).map((item) => {
              const { title, linkTo, tileSubtitle } = getItemMeta(item);
              return (
                <Link
                  key={item.path}
                  to={linkTo}
                  style={{ textDecoration: 'none', color: 'inherit', flexShrink: 0, scrollSnapAlign: 'start' }}
                >
                  <HomeTileCard imageSrc={item.artUrl} title={title} subtitle={tileSubtitle} />
                </Link>
              );
            })}
          </Box>
        </Box>
      ) : showSkeleton ? (
        <Box>
          <Box sx={{ marginBottom: 3 }}>
            <Skeleton variant="rectangular" width="100%" sx={{ paddingTop: '100%', borderRadius: 1 }} />
            <Skeleton variant="text" width="70%" height={28} sx={{ marginTop: 1.5 }} />
            <Skeleton variant="text" width="40%" />
          </Box>
          {Array.from(new Array(PREVIEW_COUNT - 1)).map((_, index) => (
            <Box key={index} sx={{ display: 'flex', gap: 1.5, alignItems: 'center', marginBottom: 1.5 }}>
              <Skeleton variant="rectangular" width={48} height={48} sx={{ borderRadius: 1 }} />
              <Skeleton variant="text" width="60%" />
            </Box>
          ))}
        </Box>
      ) : (
        <Box>{items.map((item, index) => renderRow(item, index === 0))}</Box>
      )}
    </Box>
  );
}
